import { type BoardColumn as BoardColumnData, type BoardView, type TaskView } from '@apm/contracts';

/**
 * Áp một lần kéo thả lên board **ngay tại client**, trước khi server trả lời.
 *
 * Spec mục 13 đòi mọi mutation là optimistic: thẻ phải nằm ở cột mới ngay lúc
 * thả tay, lỗi thì hoàn tác kèm toast. Chờ round-trip mới đổi UI là cảm giác
 * chậm dù đo ra chỉ 80ms.
 *
 * Hàm thuần, không đụng React — nhờ vậy test được toàn bộ phần dễ sai (tính
 * chỉ số khi kéo lên/xuống trong cùng cột) mà không cần dựng DOM.
 */
export function applyMove(
  board: BoardView,
  taskId: string,
  toStatusId: string,
  toIndex: number,
): BoardView {
  const task = findTask(board, taskId);
  if (!task) return board;

  const persistedIndex = persistedDropIndex(board, taskId, toStatusId, toIndex);
  const columns = board.columns.map((column) => ({
    ...column,
    tasks: column.tasks
      .filter((item) => item.id !== taskId)
      .map((item) => {
        if (!column.page) return item;
        let position = item.position;
        if (column.status.id === task.statusId && position > task.position) position--;
        if (column.status.id === toStatusId && position >= persistedIndex) position++;
        return { ...item, position };
      }),
  }));

  const target = columns.find((column) => column.status.id === toStatusId);
  if (!target) return board;

  const at = Math.min(Math.max(toIndex, 0), target.tasks.length);
  target.tasks = [
    ...target.tasks.slice(0, at),
    {
      ...task,
      ...(target.page ? { position: persistedIndex } : {}),
      statusId: toStatusId,
      statusName: target.status.name,
      statusCategory: target.status.category,
    },
    ...target.tasks.slice(at),
  ];

  return {
    ...board,
    columns: columns.map((column) => {
      const delta =
        task.statusId === toStatusId
          ? 0
          : column.status.id === toStatusId
            ? 1
            : column.status.id === task.statusId
              ? -1
              : 0;
      const count = column.page
        ? Math.max(0, column.count + (task.kind === 'milestone' ? 0 : delta))
        : column.tasks.filter((item) => item.kind !== 'milestone').length;
      return {
        ...column,
        count,
        ...(column.page
          ? {
              page: {
                ...column.page,
                fullTotal: Math.max(0, column.page.fullTotal + delta),
                total: Math.max(0, column.page.total + delta),
              },
            }
          : {}),
        overWipLimit: column.status.wipLimit !== null && count > column.status.wipLimit,
      };
    }),
  };
}

export function findTask(board: BoardView, taskId: string): TaskView | null {
  for (const column of board.columns) {
    const found = column.tasks.find((task) => task.id === taskId);
    if (found) return found;
  }
  return null;
}

export function columnOf(board: BoardView, taskId: string): string | null {
  return (
    board.columns.find((column) => column.tasks.some((task) => task.id === taskId))?.status.id ??
    null
  );
}

/**
 * Thứ tự cột mới sau khi chuyển một cột sang trái/phải — **trong nhóm của nó**.
 *
 * ## Vì sao phải lọc nhóm
 *
 * `APM-BOARD-a2` đổi luật ở server: một lượt sắp xếp phải gồm **đủ và đúng** tập
 * cột của một nhóm — hoặc toàn cột thuộc một sprint, hoặc toàn cột ngoài sprint.
 * Nhưng board của một sprint vẽ **cả hai nhóm cạnh nhau**, nên gửi trọn danh
 * sách đang hiển thị là gửi một danh sách trộn, và server từ chối.
 *
 * Hỏng theo kiểu tệ nhất: nút vẫn bấm được, lỗi trả về là "Dữ liệu chưa hợp lệ"
 * chung chung, và nó hỏng với **mọi** cột chứ không riêng cột vừa tạo — nên
 * người dùng đi tìm nguyên nhân ở cái cột họ vừa thêm.
 *
 * Lọc theo `sprintId` chứ không theo vị trí trên màn hình: hai nhóm vẽ liền
 * nhau nên chỉ số hiển thị không nói được cột nào thuộc nhóm nào.
 *
 * Trả `null` khi không nhúc nhích được (cột đầu nhóm kéo sang trái, cột cuối
 * kéo sang phải, hoặc cột không có trong danh sách) — chỗ gọi không phải tự
 * kiểm biên một lần nữa.
 */
export function reorderColumnWithinGroup(
  columns: readonly BoardColumnData[],
  statusId: string,
  direction: -1 | 1,
): string[] | null {
  const moving = columns.find((entry) => entry.status.id === statusId);
  if (!moving) return null;

  const group = columns.filter(
    (entry) => (entry.status.sprintId ?? null) === (moving.status.sprintId ?? null),
  );

  const at = group.findIndex((entry) => entry.status.id === statusId);
  const target = at + direction;
  if (at < 0 || target < 0 || target >= group.length) return null;

  const order = group.map((entry) => entry.status.id);
  [order[at], order[target]] = [order[target]!, order[at]!];
  return order;
}

/** Cột đứng cùng nhóm với cột này — dùng để tính biên "chuyển trái/phải". */
export function columnGroupOf(
  columns: readonly BoardColumnData[],
  status: { id: string; sprintId: string | null },
): BoardColumnData[] {
  return columns.filter((entry) => (entry.status.sprintId ?? null) === (status.sprintId ?? null));
}

/**
 * Chỗ chen vào, đếm trên danh sách **đang hiển thị** — APM-BOARD-slot2.
 *
 * ## Vì sao bỏ `dropIndex` cũ
 *
 * Bản cũ trả chỉ số trong không gian "đã gỡ thẻ nguồn ra", và nó **phụ thuộc
 * chiều kéo**: đè lên một thẻ *dưới* mình nghĩa là "chen sau thẻ ấy", đè lên
 * một thẻ *trên* mình nghĩa là "chen trước". Đó là ngữ nghĩa mượn từ
 * `SortableContext`, và nó có hai hệ quả người dùng gặp phải:
 *
 * 1. **Vị trí trên cùng không với tới được.** Kéo thẻ đầu cột xuống rồi muốn
 *    quay lại: thứ duy nhất đè được là thẻ ngay dưới, mà đè nó nghĩa là "chen
 *    sau nó" — không có cách nào diễn đạt "về lại đầu cột".
 * 2. Cùng một chỗ trên màn hình mang hai nghĩa khác nhau tuỳ hướng kéo, nên
 *    khung trống nhảy theo một luật không ai đoán được.
 *
 * Mô hình mới không hỏi "đè lên thẻ nào" mà hỏi "chen vào **khe** nào", và khe
 * thì không có chiều: `overId` là thẻ mà chỗ chen nằm **ngay trước** nó
 * (`boardCollision` chọn theo đường giữa thẻ), hoặc là cột khi chen xuống cuối.
 *
 * Chỉ số trả về đếm trên danh sách **vẫn còn** thẻ đang kéo — đúng danh sách mà
 * khung trống được vẽ vào. Đổi sang chỉ số để gửi đi là việc của `toRemovalIndex`.
 */
export function slotIndexOf(
  board: BoardView,
  overId: string,
  overIsColumn: boolean,
): { statusId: string; slotIndex: number } | null {
  if (overIsColumn) {
    const column = board.columns.find((item) => item.status.id === overId);
    return column ? { statusId: overId, slotIndex: column.tasks.length } : null;
  }

  const targetColumn = board.columns.find((column) =>
    column.tasks.some((task) => task.id === overId),
  );
  if (!targetColumn) return null;

  return {
    statusId: targetColumn.status.id,
    slotIndex: targetColumn.tasks.findIndex((task) => task.id === overId),
  };
}

/**
 * Chỗ chen (danh sách đang hiển thị) → chỉ số cho `applyMove` và cho server.
 *
 * `applyMove` **gỡ thẻ nguồn ra trước rồi mới chèn**, nên khi thẻ nguồn nằm
 * *trước* chỗ chen trong cùng một cột, mọi thứ sau nó lùi lại một bậc.
 *
 * ```
 * [A B C D], kéo A, khung giữa B và C (slot 2) ⇒ gỡ ra còn [B C D], chèn ở 1
 * [A B C D], kéo A, khung ngay trên B  (slot 1) ⇒ chèn ở 0 — **về đúng chỗ cũ**
 * ```
 *
 * Dòng thứ hai là thứ bản cũ không diễn đạt được, và là lỗi người dùng báo.
 */
export function toRemovalIndex(
  slotIndex: number,
  statusId: string,
  origin: { readonly statusId: string; readonly index: number } | null,
): number {
  if (!origin || origin.statusId !== statusId) return slotIndex;
  return origin.index < slotIndex ? slotIndex - 1 : slotIndex;
}

/** Translate a position in a loaded/filtered page to the persisted column rank. */
export function persistedDropIndex(
  view: BoardView,
  taskId: string,
  statusId: string,
  index: number,
): number {
  const column = view.columns.find((item) => item.status.id === statusId);
  if (!column?.page) return index;
  const moving = view.columns.flatMap((item) => item.tasks).find((task) => task.id === taskId);
  const sameColumn = columnOf(view, taskId) === statusId;
  const remaining = column.tasks
    .filter((task) => task.id !== taskId)
    .sort((a, b) => a.position - b.position);
  const next = remaining[index];
  if (next)
    return Math.max(
      0,
      next.position - (sameColumn && moving && moving.position < next.position ? 1 : 0),
    );
  const last = remaining.at(-1);
  if (last)
    return Math.max(
      0,
      last.position + 1 - (sameColumn && moving && moving.position < last.position ? 1 : 0),
    );
  return 0;
}
